<template>
  <div ref="container" />
</template>

<script lang="ts">
// Privacy/tracking blockers can silently drop the hCaptcha request, in which
// case neither `load` nor `error` ever fires and the widget hangs invisibly.
// Time out so the caller can surface an actionable message instead of a blank box.
const LOAD_TIMEOUT_MS = 10000;
let loadPromise: Promise<void> | null = null;
function loadScript(): Promise<void> {
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise<void>((resolve, reject) => {
    const fail = (reason: string) => {
      // Reset so a remount (e.g. after the visitor disables their blocker) retries.
      loadPromise = null;
      reject(new Error(reason));
    };
    const timeout = setTimeout(
      () => fail("hcaptcha-load-timeout"),
      LOAD_TIMEOUT_MS
    );
    const succeed = () => {
      clearTimeout(timeout);
      resolve();
    };

    const existing = document.querySelector('script[src*="js.hcaptcha.com"]');

    if (existing) {
      if (window.hcaptcha) {
        succeed();
        return;
      }
      existing.addEventListener("load", succeed);
      existing.addEventListener("error", () => fail("hcaptcha-script-error"));
      return;
    }

    window.onloadHcaptchaCallback = succeed;
    const script = document.createElement("script");
    script.src =
      "https://js.hcaptcha.com/1/api.js?onload=onloadHcaptchaCallback&render=explicit";
    script.async = true;
    script.defer = true;
    script.onerror = () => fail("hcaptcha-script-error");

    document.head.appendChild(script);
  });

  return loadPromise;
}
</script>

<script setup lang="ts">
import { useTemplateRef, watch } from "vue";

const props = defineProps<{
  siteKey: string;
  theme?: "light" | "dark" | "contrast";
  size?: "normal" | "compact" | "invisible";
  tabindex?: number;
  language?: string;
}>();

const emit = defineEmits<{
  callback: [response: string];
  errorCallback: [response: string];
  expiredCallback: [];
  loadError: [];
}>();

const container = useTemplateRef("container");

watch(container, async (node, _prevNode, onCleanup) => {
  if (!node) {
    return;
  }

  const controller = new AbortController();
  const signal = controller.signal;

  onCleanup(() => {
    controller.abort();
  });

  try {
    await loadScript();
  } catch {
    if (!signal.aborted) {
      emit("loadError");
    }
    return;
  }
  if (signal.aborted) {
    return;
  }

  const id = window.hcaptcha!.render(node, {
    sitekey: props.siteKey,
    callback: (response: string) => emit("callback", response),
    "expired-callback": () => emit("expiredCallback"),
    "error-callback": (response: string) => emit("errorCallback", response),
    theme: props.theme,
    size: props.size,
    tabindex: props.tabindex,
    language: props.language,
  });

  signal.addEventListener("abort", () => {
    window.hcaptcha!.remove(id);
  });
});
</script>
